import { Messages as CatalogMessages } from '@tableau/catalog-messages';
import {
  DataQualityType,
  getLabelValues,
  HelpTextDiv,
  InputHintDiv,
  LabelCategory,  LabelContentItem,
  LabelDialogParts,
LabelDialogType,   LabelHelpInfoPopover,
LabelStyles, LabelValueDescription,
  makeFetchInitialStateForLabels,
  mapLabelValuesToDescriptions,
  mapLabelValueToListItem,
  useSetIsLabelSevere} from '@tableau/catalog-ui-temp';
import { restApiService } from '@tableau/vizportalclient-common-components';
import {
  DropdownButtonType,
  getMaxCharValidation,
  List,
  ListItem,
  SingleSelectDropdown,
  ToggleSwitchWidget } from '@tableau/widgets';
import React from 'react';

import { ReactActionDialogBodyProps, ReactActionDialogOptions } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { useSelector } from '~src/settings/general-settings/utils';

import { Messages } from '../../../codegen/Localize';
import { TextFormatterOrAreaWidget } from '../common/TextFormatterOrAreaWidget';
import { canSubmitLabel, emptyMessageAllowed } from './canSubmitLabel';
import { dataQualityTypeToString } from './DataQualityType';
import shallowEqual = require('shallowequal');
import _ = require('underscore');

import { CompositeSessionInfoSelectors, SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { CatalogProviders } from '~src/catalog/CatalogProviders';
import { DataQualityTriggerInfo, makeFetchInitialStateForTriggers } from '~src/catalog/restApi/dataQuailtyTriggers/getDataQualityTriggersForItem';

import { LabelValueDialogState } from '../labelValues/LabelValueDialogHelper';
import { listWithLabelLink } from './LabelDialogHelpers';

const LABEL_DIALOG_TOGGLE_TEST_ID = 'LABEL_DIALOG_TOGGLE_TEST_ID';
const LABEL_DIALOG_MESSAGE_ID = 'LABEL_DIALOG_MESSAGE_ID';
const LABEL_DIALOG_DROPDOWN_TEST_ID = 'LABEL_DIALOG_DROPDOWN_TEST_ID';

export type DialogBodyProps = {
  readonly contentItem: LabelContentItem;
  readonly showLabelManagementLink?: boolean;
  readonly messageOptional?: boolean;
  readonly disabled?: boolean; // can be deleted when we remove custom sensitivity FF
  readonly allowsMulti?: boolean;
}

export type DialogState = {
  readonly luid?: string;
  readonly message?: string | null;
  readonly isActive: boolean;
  readonly isSevere: boolean;
  readonly labelValue: string;
  readonly dialogType: LabelDialogType;
}

export type ActionDialogOptions = ReactActionDialogOptions<DialogBodyProps, DialogState, unknown>;
export type DialogBodyComponentProps = ReactActionDialogBodyProps<DialogBodyProps, DialogState>;

export type LabelCategories = LabelCategory;


type LabelValueDisplay = {
  labelValueToDescription: Map<string, string>;
  listItems: Array<ListItem>;
}

export function isFormDataValidForSubmission(args: {
  initialFormData: DialogState | LabelValueDialogState;
  currentFormData: DialogState | LabelValueDialogState;
  additionalValidation: (current: DialogState | LabelValueDialogState) => boolean;
}): boolean {
  const { initialFormData, currentFormData } = args;

  const formIsDirty = !shallowEqual(initialFormData, currentFormData);
  if (!formIsDirty) {
    return false;
  }

  if (!args.additionalValidation(currentFormData)) {
    return false;
  }

  return true;
}

function getInitialTypeFromCategory(dialogType: LabelDialogType): DataQualityType {
  switch(dialogType) {
    case 'EXTRACT_REFRESH_FAILURE':
      return DataQualityType.EXTRACT_REFRESH_FAILURE;
    case 'FLOW_RUN_FAILURE':
      return DataQualityType.FLOW_RUN_FAILURE;
    case 'warning':
      return DataQualityType.WARNING;
    case 'certification':
      return DataQualityType.CERTIFIED;
    case 'sensitivity':
      return DataQualityType.SENSITIVE_DATA;
  }
}

function getLabelCategory(dialogType: LabelDialogType): LabelCategories {
  switch(dialogType) {
    case 'EXTRACT_REFRESH_FAILURE':
    case 'FLOW_RUN_FAILURE':
      return 'warning';
    default:
      return dialogType;
  }
}

function isMonitoringLabel(dialogType: LabelDialogType): boolean {
  switch(dialogType) {
    case 'EXTRACT_REFRESH_FAILURE':
    case 'FLOW_RUN_FAILURE':
      return true;
    default:
      return false;
  }
}

function allowsMultiValues(dialogType: LabelDialogType): boolean {
  switch(dialogType) {
    case 'EXTRACT_REFRESH_FAILURE':
    case 'FLOW_RUN_FAILURE':
    case 'certification':
      return false;
    default:
      // warnings and sensitivities
      return true;
  }
}

export function defaultInitialState(dialogType: LabelDialogType): DialogState {
  return {
    isActive: false,
    message: '',
    dialogType,
    isSevere: false,
    labelValue: dataQualityTypeToString(getInitialTypeFromCategory(dialogType)),
  };
}

/**
 * Data Quality Service will throw if we send an enabled with a blank note
 */
function checkIsValid(current: DialogState): boolean {
  const command = canSubmitLabel({ message: current.message, active: current.isActive, category: current.dialogType});
  return command === 'SUBMIT' || command === 'DELETE';
}


function getFormDataFromTrigger(trigger: DataQualityTriggerInfo): DialogState {
  return {
    isActive: trigger.active,
    isSevere: trigger.severe,
    message: trigger.message,
    labelValue: getLabelType(trigger.type),
    dialogType: getLabelType(trigger.type),
  };
}

function getLabelType(type: DataQualityTriggerInfo['type']){
  switch(type){
    case 'EXTRACT_REFRESH': return 'EXTRACT_REFRESH_FAILURE';
    case 'FLOW_RUN': return 'FLOW_RUN_FAILURE';
  }
}


function fetchInitialState(dialogType: LabelDialogType, initialBodyProps: DialogBodyProps) {
  const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());
  const category = getLabelCategory(dialogType);
  const isMonitoring = isMonitoringLabel(dialogType);
  return isMonitoring?
  makeFetchInitialStateForTriggers<DialogBodyProps, DialogState>(
    {
      contentItemLuid: initialBodyProps.contentItem.contentId,
      contentItemType: initialBodyProps.contentItem.contentType
    },
    getFormDataFromTrigger
  )
  :
  makeFetchInitialStateForLabels<DialogBodyProps, DialogState>({
    category,
    siteLuid: siteLuid,
    transform: (t) => ({
      isActive: t.active,
      isSevere: t.elevated,
      message: t.message,
      labelValue: t.value,
      dialogType,
    }),
  });
}

export function useLabelValues(dialogType: LabelDialogType, labelValue: string): LabelValueDisplay {
  const siteLuid = useSelector(SessionSiteSelectors.siteLuid);
  const isMonitoring = isMonitoringLabel(dialogType);
  const initialLabelValues: ListItem[] | (() => ListItem[]) = [];
  const [labelValueDisplay, setLabelValueDisplay] = React.useState<LabelValueDisplay>(
    {labelValueToDescription: new Map<string, string>(), listItems: initialLabelValues});
  React.useEffect(() => {
    let subscribed = false;
    const {makeJsonRestApiRequest} = restApiService({siteLuid});
    const fetchLabelValues = async () => {
      const category = getLabelCategory(dialogType);
      const values = await getLabelValues({ category, makeJsonRestApiRequest });

      if (!subscribed){
        setLabelValueDisplay({
          labelValueToDescription: mapLabelValuesToDescriptions(values.filter(value => isMonitoring? value.name === labelValue : !value.internal)),
          listItems: values.filter(value => isMonitoring? value.name === labelValue : !value.internal).map(mapLabelValueToListItem)
        });
      }
    };
    fetchLabelValues().catch();

    return () => { subscribed = true; };
  }, [setLabelValueDisplay, siteLuid, dialogType, labelValue, isMonitoring]);
  return labelValueDisplay;
}

function useBuildBodyProps(contentItem: LabelContentItem, dialogState: DialogState, labelValues: LabelValueDisplay): DialogBodyProps {
  const {isActive, dialogType} = dialogState;
  const isSiteOrServerAdmin = useSelector(CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector);
  const isCatalogingOnSiteEnabled = useSelector(SessionSiteSelectors.catalogingOnSiteEnabled);
  const allowsMulti = allowsMultiValues(dialogType);
  const messageOptional = emptyMessageAllowed(dialogType);
  const hasLabelValues = !!labelValues.listItems && labelValues.listItems.length > 0;
  const disabled = hasLabelValues && !isActive;

  return {
    contentItem,
    showLabelManagementLink: isSiteOrServerAdmin && isCatalogingOnSiteEnabled && FeatureFlags.labelManagementEnabled,
    messageOptional,
    disabled,
    allowsMulti
  };
}

export function LabelDialogBody({ updateDialogState, dialogState, contentItem}: DialogBodyComponentProps): React.ReactElement {
  const {message, isActive, dialogType, isSevere: isSevereState, labelValue} = dialogState;

  const siteUrls = useSelector(siteUrlsSelector);

  const handleMessageChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) =>
    updateDialogState({ message: e.currentTarget.value }), [updateDialogState]);
  const { message: dialogMessage, valid } = getMaxCharValidation(message ?? '', LabelStyles.MESSAGE_MAXIMUM_LENGTH);
  const labelValues = useLabelValues(dialogType, labelValue);
  const {showLabelManagementLink, messageOptional, disabled, allowsMulti} = useBuildBodyProps(contentItem, dialogState, labelValues);

  const [SevereWarningContent, isSevere] = useSetIsLabelSevere({ disabled, initial: isSevereState });

  return (
    <>
    {contentItem.contentName ?
      <LabelStyles.ContentItemNameDiv>
        <LabelDialogParts.ContentItemIconDetails {...contentItem} />
        {contentItem.contentName}
      </LabelStyles.ContentItemNameDiv>
      : null}

      <LabelHelpInfoPopover dialogType={dialogType} />

      <ToggleSwitchWidget
        textAlign={'right'}
        onChange={(e) => updateDialogState({ isActive: e.target.checked })}
        checked={isActive}
        children={LabelDialogParts.getToggleLabel(dialogType)}
        style={{ justifyContent: 'flex-start', marginTop: '24px' }}
        testId={dialogType + LABEL_DIALOG_TOGGLE_TEST_ID}
        id={dialogType + LABEL_DIALOG_TOGGLE_TEST_ID}
      />

      {allowsMulti  &&
        <>
        <div style={{paddingTop: '24px'}}>
            <SingleSelectDropdown
              testId={dialogType + LABEL_DIALOG_DROPDOWN_TEST_ID}
              type={DropdownButtonType.Outline}
              list={<List items={labelValues.listItems.concat(showLabelManagementLink? listWithLabelLink() : [])} />}
              disabled={disabled}
              value={labelValue}
              onChange={(labelValue?: string) => (labelValue === CatalogMessages.labelManagementLink())?
                window.open(siteUrls.labelsUrl(), '_blank') : updateDialogState({ labelValue }) } //the link isn't a valid label value
              style={LabelStyles.dropDownStyle}
              label={<HelpTextDiv disabled={disabled}>{Messages.selectLabel()}</HelpTextDiv>}
            />
        </div>
        </>
      }
      <LabelValueDescription showLink={!allowsMulti && showLabelManagementLink} disabled={disabled} description={labelValues.labelValueToDescription.get(labelValue)} />

      {getLabelCategory(dialogType) === 'warning' && SevereWarningContent}

      <TextFormatterOrAreaWidget
        label={
        <InputHintDiv disabled={disabled}>
          {messageOptional? Messages.labelMessageOptional(): Messages.labelMessageRequired()}
          </InputHintDiv>}
        onChange={handleMessageChange}
        id={dialogType + LABEL_DIALOG_MESSAGE_ID}
        testId={dialogType + LABEL_DIALOG_MESSAGE_ID}
        disabled={disabled}
        valid={valid}
        message={dialogMessage}
        style={LabelStyles.markupEditorStyle}
        markup={message}
      />
    </>
  );
}

function LabelDialogBodyWrapper(props: DialogBodyComponentProps) {
  return (
    <CatalogProviders>
      <LabelDialogBody {...props} />
    </CatalogProviders>
  );
}

export function getActionDialogOptions(args: {
  initialBodyProps: DialogBodyProps;
  initialFormData: DialogState;
  doAction: (formData: DialogState) => Promise<unknown>;
  dialogType: LabelDialogType;
}): ActionDialogOptions {
  const { initialBodyProps, initialFormData, doAction, dialogType } = args;

  return {
    title: LabelDialogParts.getDialogTitle(dialogType),
    fetchInitialState: fetchInitialState(dialogType, initialBodyProps),
    bodyComponent: LabelDialogBodyWrapper,
    initialDialogState: initialFormData,
    initialBodyProps: initialBodyProps,
    actionName: Messages.save(),
    action: (_, dialogState, __) => doAction(dialogState),
    actionDisabled: (_, dialogState, fetchedState) => !isFormDataValidForSubmission({
      initialFormData:  fetchedState ?? initialFormData,
      currentFormData: dialogState,
      additionalValidation: checkIsValid,
    }),
    showResetButtonUsingState: defaultInitialState(dialogType),
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width500px,
    dialogBodyStyle: {padding: '16px 18px'},
    initiallyFocusedElement: (dialog) => dialog.querySelector(`#${dialogType + LABEL_DIALOG_TOGGLE_TEST_ID}`),
  };
}
